// ─────────────────────────────────────────────
//  MobileRoomStatus — the PROVIDER Room Status screen: ONE room.
//  (design: docs/specs/2026-09-07-mobile-room-status-design.md §6.3)
//
//  A CRNA, or an MD running a solo case, stamping their own room: header
//  ("WOR4 ROOM STATUS" + "SURGEON: Gaskins"), the two case boundaries as
//  buttons that show their stamped time once pressed, `New case`, the four
//  phase rows, then the message box.
//
//  ⚠️ PRESENTATIONAL ONLY — this file renders and delegates; it never touches
//  Firestore. Every value arrives as a prop and every write leaves through a
//  callback, exactly like MobileRoomCard. The orchestration (stamp → phaseStatus
//  → updateRoomStatus) is the hook's job, and putting any of it here would give
//  this surface a second opinion about what a phase means.
//
//  Props (the wiring contract):
//    { room, orCase, mode, surgeon, ageMinutesFor, onStamp(phaseId),
//      onClear(phaseId), onBoundary(eventId), onAck(phaseId), onNewCase(),
//      busy, messageBox, alertButtons }
//
//  `onClear(phaseId)` is the SECOND tap on a phase that already has a time
//  (owner, 2026-09-11). It un-stamps rather than re-stamping; see PhaseRow.
//
//  `alertButtons` is an already-rendered node, PLACED and not built — the same
//  contract as `messageBox`, and for the same reason. Owner, 2026-09-07: "beside
//  the room status place a emergency button in red, use the emergency system
//  that is already in place." That system is MobileAlertButtons, which owns the
//  two-tap arm/confirm that stops a pocket-fire, the blink on the shared flash
//  clock, and the alarm/vibration path in AlertContext. Rebuilding a red button
//  here would be a SECOND way to raise an emergency, and the second one would
//  be the one missing the confirm.
//
//  `ageMinutesFor(phaseId)` — minutes since that phase's stamp, or null. This
//  page is one room, so the id alone identifies the row (the supervisor page
//  binds its roomId in first). The AGE is what stops "Induction 07:40" from
//  still reading as *Induction* at 14:00 (§12); without it every row asserts
//  its phase forever, which is the confidently-wrong board this design exists
//  to prevent.
//
//  `messageBox` is an already-rendered node. It is PLACED, not built: the
//  merge/normalise rules for messages live in domain/roomMessageBox (§7), and
//  a second implementation inside a screen is how the two surfaces would come
//  to disagree about which message matters.
//
//  ⚠️ No 100vh anywhere. It measures the layout viewport, so on iOS the bottom
//  of the page sits underneath the browser/tab bar and the last control on this
//  screen — New case, or the final phase row — becomes untappable.
// ─────────────────────────────────────────────
import React from "react";

import mobile, { px } from "../../theme/tokens.mobile";
import { PhaseGrid, StatusHeader } from "./roomStatusPieces";

const { color, space, radius, layout } = mobile;

const s = {
  page: {
    // Grows with its content and scrolls in the shell. Never a viewport unit.
    width: "100%",
    maxWidth: layout.contentMaxWidth,
    margin: "0 auto",
    padding: px(space.md),
    boxSizing: "border-box",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: px(space.md),
  },
  panel: {
    background: color.surface.ink,
    border: `1px solid ${color.border.card}`,
    borderRadius: px(radius.lg),
    padding: px(space.md),
    minWidth: 0,
  },
  // Holds the compact alert squares hard against the top-right of the page.
  alertRow: { display: "flex", justifyContent: "flex-end", minWidth: 0 },
  refusal: {
    color: color.text.muted,
    fontSize: 14,
    lineHeight: 1.4,
  },
};

export default function MobileRoomStatus({
  room = null,
  orCase = null,
  mode = null,
  surgeon = "",
  ageMinutesFor,
  onStamp,
  onClear,
  onBoundary,
  onAck,
  onNewCase,
  busy = false,
  messageBox = null,
  alertButtons = null,
  breakBox = null,
}) {
  // A screen with nothing to stamp must SAY so. Rendering an empty grid of
  // pending phases for a provider who has no room would invite four taps that
  // can only fail, and a control that silently does nothing reads as broken.
  if (!room) {
    return (
      <div style={s.page} data-testid="room-status-provider">
        <section style={s.panel}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 900, letterSpacing: "0.04em", color: color.text.primary }}>
            ROOM STATUS
          </h1>
          <p style={s.refusal} data-testid="room-status-no-room">
            No room is assigned to you right now, so there is nothing to stamp.
          </p>
        </section>
        {messageBox}
      </div>
    );
  }

  const c = orCase || {};

  return (
    <div style={s.page} data-testid="room-status-provider">
      {/* ⚠️ THE ALERTS SIT AT THE TOP, AND SMALL. Owner asked for them reduced to
          two coloured squares up here rather than a full card lower down. That
          is the right trade while nothing is wrong: the page is about the case,
          and the alarm should be findable without competing with it.

          It is the SAME component, in a compact variant — not a second pair of
          buttons. The two-tap arm/confirm that stops a pocket-fire, the blink,
          and the whole AlertContext path live in there, and MobileAlertButtons
          expands itself back to full width the moment an alert is armed or
          live, because "tap again to confirm" cannot be read off a 44px square. */}
      {alertButtons && <div style={s.alertRow}>{alertButtons}</div>}
      <section style={s.panel} aria-label={`${room.name} room status`}>
        <StatusHeader
          variant="page"
          roomName={room.name}
          surgeon={surgeon}
          timeIn={c.timeIn ?? null}
          timeOut={c.timeOut ?? null}
          mode={mode}
          onBoundary={onBoundary}
          onNewCase={onNewCase}
          busy={busy}
        />
        {/* One PhaseGrid, one PhaseRow, both screens. In provider mode the
            phase cell is the button and the ack cell is static text — a
            provider never acknowledges their own stamp, which in a solo case
            (providerId === attendingId) is the ONLY thing preventing it: the
            rules cannot express "not the same human" (§5, known gap). */}
        <PhaseGrid
          phases={c.phases}
          mode={mode}
          onStamp={onStamp}
          onClear={onClear}
          onAck={onAck}
          ageMinutesFor={ageMinutesFor}
          busy={busy}
          caption={`${room.name} case phases`}
        />
      </section>
      {/* ⚠️ BEFORE THE MESSAGES, AFTER THE CASE. Owner, 2026-09-07: "add a break
          system in the room status page before the message box." It sits below
          the phases because the case is what this screen is for, and above the
          messages because a break is something you DO and a message is
          something you read. */}
      {breakBox}
      {messageBox}
    </div>
  );
}
